const SITEKEY = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();

function isLoopbackHost(): boolean {
  try {
    const h = typeof location !== 'undefined' ? location.hostname : '';
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
  } catch {
    return false;
  }
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, opts: Record<string, unknown>) => string;
      execute: (
        widgetId: string,
        opts?: {
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
      ) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let widgetId: string | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no_window'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_script_failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function ensureInvisibleWidget(): Promise<void> {
  if (!SITEKEY || typeof window === 'undefined') {
    return Promise.resolve();
  }
  return loadTurnstileScript().then(() => {
    if (widgetId != null || !window.turnstile) return;
    let el = document.getElementById('p2t-turnstile-anchor');
    if (!el) {
      el = document.createElement('div');
      el.id = 'p2t-turnstile-anchor';
      el.className = 'fixed left-0 top-0 -z-10 h-px w-px overflow-hidden opacity-0';
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
    }
    widgetId = window.turnstile.render(el, {
      sitekey: SITEKEY,
      size: 'invisible',
    });
  });
}

/**
 * Returns a fresh Turnstile token when `VITE_TURNSTILE_SITE_KEY` is set; otherwise `undefined`
 * (server skips verification when `TURNSTILE_SECRET_KEY` is unset).
 */
export async function getTurnstileTokenForEnqueue(): Promise<string | undefined> {
  if (!SITEKEY) {
    return undefined;
  }
  // Local Playwright / netlify dev: avoid loading Cloudflare on loopback (hangs without real tokens).
  if (isLoopbackHost()) {
    return undefined;
  }
  await ensureInvisibleWidget();
  if (!window.turnstile || !widgetId) {
    return undefined;
  }
  return new Promise((resolve) => {
    window.turnstile!.execute(widgetId!, {
      callback: (token: string) => resolve(token),
      'error-callback': () => resolve(undefined),
      'expired-callback': () => resolve(undefined),
    });
  });
}
